import { useState } from "react";
import { Users, Ticket, Trophy, Crown, Bell, Gift, BarChart3, Database, FileText, FileJson, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadExport } from "@/lib/download";
import { AdminHeader } from "@/components/admin/ui";

const datasets = [
  { key: "customers", label: "Customers", desc: "All users with membership info", icon: Users, color: "#00e5ff" },
  { key: "bookings", label: "Bookings", desc: "All bookings with user & station details", icon: Ticket, color: "#00ff9d" },
  { key: "tournaments", label: "Tournaments", desc: "All tournaments & participants", icon: Trophy, color: "#ffb020" },
  { key: "memberships", label: "Memberships", desc: "All subscription records", icon: Crown, color: "#8b5cf6" },
  { key: "notifications", label: "Notifications", desc: "All sent notifications", icon: Bell, color: "#ff2e97" },
  { key: "rewards", label: "Rewards", desc: "Rewards & claims", icon: Gift, color: "#00ff9d" },
  { key: "analytics", label: "Analytics", desc: "Business metrics snapshot", icon: BarChart3, color: "#00e5ff" },
  { key: "complete", label: "Complete Export", desc: "Everything in one file", icon: Database, color: "#ffb020" },
];

export default function AdminDataExport() {
  const [busy, setBusy] = useState(null);

  const run = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      await downloadExport(key, format);
      toast.success(`${key} exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally { setBusy(null); }
  };

  return (
    <div>
      <AdminHeader title="Data Export" subtitle="Download your data as CSV (Excel-ready) or JSON." />
      <div className="glass rounded-xl p-4 mb-6 text-sm text-slate-400">
        CSV files open directly in Microsoft Excel, Google Sheets or Numbers. Choose JSON for structured/programmatic use.
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {datasets.map((d) => (
          <div key={d.key} className="glass glass-hover rounded-2xl p-6 flex flex-col" data-testid={`export-card-${d.key}`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${d.color}1f`, border: `1px solid ${d.color}55` }}>
              <d.icon className="w-6 h-6" style={{ color: d.color }} />
            </div>
            <h3 className="font-display text-lg font-bold text-white">{d.label}</h3>
            <p className="text-slate-400 text-sm flex-1 mb-4">{d.desc}</p>
            <div className="flex gap-2">
              <button onClick={() => run(d.key, "csv")} disabled={busy === `${d.key}-csv`}
                className="neon-btn flex-1 py-2.5 text-sm" data-testid={`export-${d.key}-csv`}>
                {busy === `${d.key}-csv` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} CSV
              </button>
              <button onClick={() => run(d.key, "json")} disabled={busy === `${d.key}-json`}
                className="neon-outline-btn flex-1 py-2.5 text-sm" data-testid={`export-${d.key}-json`}>
                {busy === `${d.key}-json` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileJson className="w-4 h-4" />} JSON
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
